// frontend/src/context/AlertContext.js
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-06-24
// Updated:      2026-01-16
//
// Description:  Global alert context with polling, authenticated loading, dismissal, and custom trigger support.
//               Uses canonical endpoint /api/alerts/active (tenant-safe + auth hardened).
//
// Hard rule:
//   - Do NOT depend on axiosInstance here (baseURL/proxy/interceptors drift breaks alerts silently).
//   - Use fetch + explicit Authorization header (matches your working browser console test).

import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";

const AlertContext = createContext(null);

export const AlertProvider = ({ children }) => {
  const [alerts, setAlerts] = useState([]);

  const intervalRef = useRef(null);
  const mountedRef = useRef(false);

  const getToken = () =>
    localStorage.getItem("anpr_token") || sessionStorage.getItem("anpr_token") || "";

  const onLoginRoute = () => window.location.pathname === "/login";

  const fetchAlerts = useCallback(async () => {
    if (onLoginRoute()) return;

    const jwt = getToken();
    if (!jwt) return; // don't clear; just skip

    try {
      const r = await fetch("/api/alerts/active?skip=0&limit=100", {
        method: "GET",
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (r.status === 401 || r.status === 403) {
        setAlerts([]);
        return;
      }

      if (!r.ok) {
        const body = await r.text().catch(() => "");
        console.warn("Alert fetch failed:", r.status, body?.slice?.(0, 200));
        return;
      }

      const data = await r.json();
      const arr = Array.isArray(data) ? data : [];

      setAlerts((prev) => {
        const prevIds = prev.map((a) => a.id).join(",");
        const nextIds = arr.map((a) => a.id).join(",");
        return prevIds !== nextIds ? arr : prev;
      });
    } catch (e) {
      console.warn("Alert fetch crashed:", e?.message || e);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    const tick = async () => {
      if (!mountedRef.current) return;
      await fetchAlerts();
    };

    tick();
    intervalRef.current = setInterval(tick, 30000);

    const onFocus = () => tick();
    const onVisibility = () => {
      if (document.visibilityState === "visible") tick();
    };
    const onPopState = () => tick();
    const onStorage = (e) => {
      if (!e) return;
      if (e.key === "anpr_token") tick();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("popstate", onPopState);
    window.addEventListener("storage", onStorage);

    return () => {
      mountedRef.current = false;

      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("popstate", onPopState);
      window.removeEventListener("storage", onStorage);

      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [fetchAlerts]);

  const dismissAlert = useCallback(
    async (id) => {
      // Optimistic UI
      setAlerts((prev) => prev.filter((a) => a.id !== id));

      if (onLoginRoute()) return;

      const jwt = getToken();
      if (!jwt) return;

      try {
        const r = await fetch(`/api/alerts/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${jwt}`,
          },
        });

        if (r.status === 401 || r.status === 403) {
          setAlerts([]);
          return;
        }

        if (!r.ok) {
          const body = await r.text().catch(() => "");
          console.warn("Alert dismiss failed:", r.status, body?.slice?.(0, 200));
          // re-sync
          await fetchAlerts();
        }
      } catch (e) {
        console.warn("Alert dismiss crashed:", e?.message || e);
        // re-sync best-effort
        try {
          await fetchAlerts();
        } catch {
          // ignore
        }
      }
    },
    [fetchAlerts]
  );

  const triggerAlert = useCallback((message, severity = "info") => {
    const newAlert = {
      id: Date.now(),
      title: "System",
      message,
      severity,
      created_at: new Date().toISOString(),
    };
    setAlerts((prev) => [newAlert, ...prev]);
  }, []);

  return (
    <AlertContext.Provider value={{ alerts, dismissAlert, triggerAlert }}>
      {children}
    </AlertContext.Provider>
  );
};

export const useAlerts = () => {
  const ctx = useContext(AlertContext);
  if (!ctx) throw new Error("useAlerts must be used within AlertProvider");
  return ctx;
};

export const useAlert = useAlerts;
